import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, ListCommanWay, UpdateCommanWay, GetCommanWay } from "API/commonApi";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";
import handleWheel from "utils/handleOnWheel";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  CalendarIcon,
  XMarkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { Button, Chip } from "@heroui/react";

// Day keys matching DB columns
const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

// Select styles matching AddBooking
const bookingSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: "10px",
    borderColor: state.isFocused ? "#6366f1" : "#e2e8f0",
    minHeight: "28px",
    fontSize: "12px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(99,102,241,0.15)" : "none",
    "&:hover": { borderColor: "#cbd5e1" },
    backgroundColor: "white",
    transition: "all 0.15s ease",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base: any) => ({ ...base, height: "28px" }),
  dropdownIndicator: (base: any) => ({ ...base, padding: "4px" }),
  clearIndicator: (base: any) => ({ ...base, padding: "4px" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#6366f1" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    "&:active": { backgroundColor: "#e0e7ff" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.1)", zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  menuList: (base: any) => ({ ...base, padding: "4px" }),
  singleValue: (base: any) => ({ ...base, color: "#334155" }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
};

// MInput - compact inline label + input (matches AddBooking)
const MInput = ({
  label,
  name,
  value,
  onChange,
  onBlurExtra,
  type = "text",
  disabled = false,
  className = "",
  placeholder = "",
}: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <input
      type={type}
      className={`flex-1 min-w-0 px-2 py-[3px] rounded-lg border text-[13px] outline-none transition-all ${
        disabled
          ? "border-slate-200 bg-slate-100 text-slate-500"
          : "border-slate-200 bg-white focus:border-primary2 focus:ring-2 focus:ring-primary2/20 text-slate-700"
      }`}
      name={name}
      value={value || ""}
      onChange={onChange}
      onBlur={(e) => {
        onBlurExtra && onBlurExtra(e);
      }}
      onWheel={handleWheel}
      placeholder={placeholder || label}
      disabled={disabled}
    />
  </div>
);

// MSelect - compact inline label + select (matches AddBooking)
const MSelect = ({ label, name, options, value, onChange, disabled = false, className = "" }: any) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <label className="text-[11px] font-medium text-slate-500 whitespace-nowrap min-w-[52px] text-right">{label}</label>
    <Select
      className="flex-1 min-w-0"
      options={options}
      value={value}
      onChange={onChange}
      isDisabled={disabled}
      styles={bookingSelectStyles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  </div>
);

const RegularPickupList: React.FC = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [scheduleSummary, setScheduleSummary] = useState<any[]>([]);
  const [showSummary, setShowSummary] = useState(false);

  // Form state - matching DB columns exactly
  const [formData, setFormData] = useState<any>({
    id: null,
    templateName: "",
    // Days checkboxes
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    // Schedule
    preferredTime: "10:00",
    whatsappNumbers: [],
    // Subbranch
    subbranch: null as any,
    // Pickup fields
    customer_code: "",
    product_name: "",
    quantity: 1,
    weight: 0,
    // Customer details
    customer_mobile: "",
    customer_pincode: "",
    customer_sh: "",
    customer_city: "",
    customer_state: "",
    customer_country: "INDIA",
    customer_address: "",
    // Shipper details
    shipper_pincode: "",
    shipper_mobile: "",
    shipper_sh: "",
    shipper_city: "",
    shipper_state: "",
    shipper_country: "INDIA",
    shipper_address: "",
    // Consignee details
    consignee_pincode: "",
    consignee_mobile: "",
    consignee_cgnee: "",
    consignee_city: "",
    consignee_state: "",
    consignee_country: "INDIA",
    consignee_address: "",
    consignee_company: "",
    // Status
    isActive: true,
  });

  // Customer options for select
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  // Product options for select
  const [productOptions, setProductOptions] = useState<any[]>([]);
  // Subbranch options for select
  const [subbranchOptions, setSubbranchOptions] = useState<any[]>([]);

  // Pincode lookup mutation
  const { mutate: lookupPincode } = useMutation(
    (data: { pincode: string; field: "customer" | "shipper" | "consignee" }) =>
      GetCommanWay(`/admin/address/${data.pincode}`, {}, {}),
    {
      onSuccess: (data: any, variables) => {
        if (data.status === "SUCCESS" && data.data) {
          const prefix = variables.field;
          setFormData((prev: any) => ({
            ...prev,
            [`${prefix}_city`]: data.data.city || "",
            [`${prefix}_state`]: data.data.state || "",
            [`${prefix}_country`]: data.data.country || "INDIA",
          }));
        }
      },
    }
  );

  // WhatsApp numbers input state
  const [whatsappInput, setWhatsappInput] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [pageCount, setPageCount] = useState(1);
  const [itemCount, setItemCount] = useState(0);

  // Fetch subbranches for dropdown
  useQuery(
    ["subbranches_for_regular_pickup"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const options = (data.data?.data || []).map((p: any) => ({
            value: p.id,
            label: p.branch_name,
          }));
          setSubbranchOptions(options);
        }
      },
    }
  );

  // Fetch customers for dropdown
  useQuery(
    ["customers_for_regular_pickup"],
    () => ListCommanWay("/admin/customer/list", {}, { paginate: 500 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const options = (data.data?.data || []).map((c: any) => ({
            value: c.code,
            label: `${c.code} - ${c.sh}`,
            data: c,
          }));
          setCustomerOptions(options);
        }
      },
    }
  );

  // Fetch products for dropdown
  useQuery(
    ["products_for_regular_pickup"],
    () => ListCommanWay("/admin/product/list", {}, { paginate: 100 }),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          const options = (data.data?.data || []).map((p: any) => ({
            value: p.product_name,
            label: `${p.product_code} - ${p.product_name}`,
          }));
          setProductOptions(options);
        }
      },
    }
  );

  // Fetch regular pickups list
  const { isLoading, refetch } = useQuery(
    ["regular_pickup_list"],
    () =>
      ListCommanWay("/admin/regular-pickup/list", {}, {
        page: currentPage,
        paginate: perPage,
        order: [["id", "DESC"]],
      }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          setIsFirstLoad(false);
          setTableData(data.data?.data || []);
          setCurrentPage(data.data?.paginator?.currentPage || 1);
          setPerPage(data.data?.paginator?.perPage || 25);
          setPageCount(data.data?.paginator?.pageCount || 1);
          setItemCount(data.data?.paginator?.itemCount || 0);
        }
      },
    }
  );

  // Fetch schedule summary
  const { refetch: refetchSummary } = useQuery(
    ["regular_pickup_schedule_summary"],
    () => GetCommanWay("/admin/regular-pickup/schedule-summary", {}, {}),
    {
      enabled: showSummary,
      onSuccess(data) {
        if (data.status === "SUCCESS") {
          setScheduleSummary(data.data || []);
        }
      },
    }
  );

  // Create mutation
  const { mutate: createMutate, isLoading: isCreating } = useMutation(
    (data: any) => CreateCommanWay({ url: "/admin/regular-pickup/create", data }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Regular pickup template created successfully", "success");
          setIsModalOpen(false);
          resetForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to create", "error");
        }
      },
      onError: (error: any) => {
        UseToast(error.message || "Failed to create", "error");
      },
    }
  );

  // Update mutation
  const { mutate: updateMutate, isLoading: isUpdating } = useMutation(
    (data: any) => UpdateCommanWay({ url: `/admin/regular-pickup/update/${data.id}`, data: data.payload }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Regular pickup template updated successfully", "success");
          setIsModalOpen(false);
          resetForm();
          refetch();
        } else {
          UseToast(data.message || "Failed to update", "error");
        }
      },
      onError: (error: any) => {
        UseToast(error.message || "Failed to update", "error");
      },
    }
  );

  // Delete mutation
  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation(
    (ids: number[]) => CreateCommanWay({ url: "/admin/regular-pickup/deleteMany", data: { ids } }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Deleted successfully", "success");
          setSelectedIds([]);
          refetch();
        } else {
          UseToast(data.message || "Failed to delete", "error");
        }
      },
      onError: (error: any) => {
        UseToast(error.message || "Failed to delete", "error");
      },
    }
  );

  // Manual generate mutation
  const { mutate: generateMutate, isLoading: isGenerating } = useMutation(
    (id: number) => CreateCommanWay({ url: `/admin/regular-pickup/generate/${id}`, data: {} }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Pickup generated successfully! WhatsApp notification sent.", "success");
          refetch();
        } else {
          UseToast(data.message || "Failed to generate pickup", "error");
        }
      },
      onError: (error: any) => {
        UseToast(error.message || "Failed to generate pickup", "error");
      },
    }
  );

  const resetForm = () => {
    setFormData({
      id: null,
      templateName: "",
      sunday: false,
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      preferredTime: "10:00",
      whatsappNumbers: [],
      subbranch: null,
      customer_code: "",
      product_name: "",
      quantity: 1,
      weight: 0,
      customer_mobile: "",
      customer_pincode: "",
      customer_sh: "",
      customer_city: "",
      customer_state: "",
      customer_country: "INDIA",
      customer_address: "",
      shipper_pincode: "",
      shipper_mobile: "",
      shipper_sh: "",
      shipper_city: "",
      shipper_state: "",
      shipper_country: "INDIA",
      shipper_address: "",
      consignee_pincode: "",
      consignee_mobile: "",
      consignee_cgnee: "",
      consignee_city: "",
      consignee_state: "",
      consignee_country: "INDIA",
      consignee_address: "",
      consignee_company: "",
      isActive: true,
    });
    setWhatsappInput("");
    setIsEditMode(false);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (row: any) => {
    setFormData({
      id: row.id,
      templateName: row.templateName || "",
      sunday: !!row.sunday,
      monday: !!row.monday,
      tuesday: !!row.tuesday,
      wednesday: !!row.wednesday,
      thursday: !!row.thursday,
      friday: !!row.friday,
      saturday: !!row.saturday,
      preferredTime: row.preferredTime || "10:00",
      whatsappNumbers: Array.isArray(row.whatsappNumbers)
        ? row.whatsappNumbers
        : typeof row.whatsappNumbers === "string" && row.whatsappNumbers.trim()
          ? row.whatsappNumbers.split(",").map((n: string) => n.trim()).filter(Boolean)
          : [],
      subbranch: row.subbranch
        ? subbranchOptions.find((o: any) => o.value === row.subbranch) || { value: row.subbranch, label: `Branch ${row.subbranch}` }
        : null,
      customer_code: row.customer_code || "",
      product_name: row.product_name || "",
      quantity: row.quantity || 1,
      weight: row.weight || 0,
      customer_mobile: row.customer_mobile || "",
      customer_pincode: row.customer_pincode || "",
      customer_sh: row.customer_sh || "",
      customer_city: row.customer_city || "",
      customer_state: row.customer_state || "",
      customer_country: row.customer_country || "INDIA",
      customer_address: row.customer_address || "",
      shipper_pincode: row.shipper_pincode || "",
      shipper_mobile: row.shipper_mobile || "",
      shipper_sh: row.shipper_sh || "",
      shipper_city: row.shipper_city || "",
      shipper_state: row.shipper_state || "",
      shipper_country: row.shipper_country || "INDIA",
      shipper_address: row.shipper_address || "",
      consignee_pincode: row.consignee_pincode || "",
      consignee_mobile: row.consignee_mobile || "",
      consignee_cgnee: row.consignee_cgnee || "",
      consignee_city: row.consignee_city || "",
      consignee_state: row.consignee_state || "",
      consignee_country: row.consignee_country || "INDIA",
      consignee_address: row.consignee_address || "",
      consignee_company: row.consignee_company || "",
      isActive: row.isActive !== false,
    });
    setWhatsappInput("");
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSelectChange = (name: string, option: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [name]: option,
    }));
  };

  const handleCustomerSelect = (option: any) => {
    if (option?.data) {
      const c = option.data;
      const newData: any = {
        customer_code: option.value,
        templateName: formData.templateName || `${option.value} - Regular Pickup`,
        // Fill customer details
        customer_sh: c.sh || c.company_name || c.name || "",
        customer_address: c.add || c.address || "",
        customer_mobile: c.phone || c.mobile || "",
        // Also fill consignee (deliver to customer)
        consignee_cgnee: c.sh || c.company_name || c.name || "",
        consignee_address: c.add || c.address || "",
        consignee_mobile: c.phone || c.mobile || "",
      };
      
      // Lookup city from pincode if available
      const pincode = c.pin || c.pincode || "";
      if (pincode && pincode.length >= 6) {
        newData.customer_pincode = pincode;
        newData.consignee_pincode = pincode;
        lookupPincode({ pincode, field: "customer" });
        lookupPincode({ pincode, field: "consignee" });
      }
      
      setFormData((prev: any) => ({ ...prev, ...newData }));
    }
  };

  const handleSubmit = () => {
    if (!formData.templateName) {
      UseToast("Template name is required", "error");
      return;
    }
    if (!formData.customer_code) {
      UseToast("Customer code is required", "error");
      return;
    }
    // Check at least one day is selected
    const hasDay = DAYS.some(day => formData[day]);
    if (!hasDay) {
      UseToast("Please select at least one schedule day", "error");
      return;
    }

    const payload = {
      ...formData,
      subbranch: formData.subbranch ? formData.subbranch.value : null,
      whatsappNumbers: formData.whatsappNumbers || [],
      quantity: formData.quantity ? Number(formData.quantity) : 1,
      weight: formData.weight ? Number(formData.weight) : 0,
      // Ensure booleans for days
      sunday: !!formData.sunday,
      monday: !!formData.monday,
      tuesday: !!formData.tuesday,
      wednesday: !!formData.wednesday,
      thursday: !!formData.thursday,
      friday: !!formData.friday,
      saturday: !!formData.saturday,
      isActive: !!formData.isActive,
    };

    if (isEditMode && formData.id) {
      updateMutate({ id: formData.id, payload });
    } else {
      createMutate(payload);
    }
  };

  // Add WhatsApp number to list
  const addWhatsappNumber = () => {
    if (whatsappInput && whatsappInput.length >= 10) {
      const number = whatsappInput.startsWith("+") ? whatsappInput : `+91${whatsappInput}`;
      if (!formData.whatsappNumbers.includes(number)) {
        setFormData((prev: any) => ({
          ...prev,
          whatsappNumbers: [...prev.whatsappNumbers, number],
        }));
      }
      setWhatsappInput("");
    }
  };

  // Remove WhatsApp number from list
  const removeWhatsappNumber = (number: string) => {
    setFormData((prev: any) => ({
      ...prev,
      whatsappNumbers: prev.whatsappNumbers.filter((n: string) => n !== number),
    }));
  };

  // Toggle all weekdays (Mon-Sat)
  const toggleWeekdays = () => {
    const allWeekdays = formData.monday && formData.tuesday && formData.wednesday && 
                        formData.thursday && formData.friday && formData.saturday;
    setFormData((prev: any) => ({
      ...prev,
      monday: !allWeekdays,
      tuesday: !allWeekdays,
      wednesday: !allWeekdays,
      thursday: !allWeekdays,
      friday: !allWeekdays,
      saturday: !allWeekdays,
    }));
  };

  // Toggle all days
  const toggleAllDays = () => {
    const allDays = DAYS.every(day => formData[day]);
    const newValue = !allDays;
    setFormData((prev: any) => ({
      ...prev,
      sunday: newValue,
      monday: newValue,
      tuesday: newValue,
      wednesday: newValue,
      thursday: newValue,
      friday: newValue,
      saturday: newValue,
    }));
  };

  const handleDelete = () => {
    if (selectedIds.length === 0) {
      UseToast("Please select items to delete", "error");
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} item(s)?`)) {
      deleteMutate(selectedIds);
    }
  };

  const handleGenerate = (id: number) => {
    if (window.confirm("Generate pickup now and send WhatsApp notification?")) {
      generateMutate(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === tableData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(tableData.map((row: any) => row.id));
    }
  };

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const getScheduleDisplay = (row: any) => {
    const dayLabels: Record<string, string> = {
      sunday: "Sun",
      monday: "Mon",
      tuesday: "Tue",
      wednesday: "Wed",
      thursday: "Thu",
      friday: "Fri",
      saturday: "Sat",
    };
    const activeDays = DAYS.filter(day => row[day]).map(day => dayLabels[day]);
    return activeDays.length > 0 ? activeDays.join(", ") : "-";
  };

  useEffect(() => {
    refetch();
  }, [currentPage]);

  useEffect(() => {
    if (showSummary) {
      refetchSummary();
    }
  }, [showSummary]);

  return (
    <>
      {(isLoading || isCreating || isUpdating || isDeleting || isGenerating) && <Spinner />}

      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold font-inter">Regular Pickup Management</h1>
          <div className="flex gap-2">
            <Button
              color="secondary"
              size="sm"
              startContent={<CalendarIcon className="h-4 w-4" />}
              onPress={() => setShowSummary(!showSummary)}
            >
              {showSummary ? "Hide" : "View"} Schedule
            </Button>
            {selectedIds.length > 0 && (
              <Button
                color="danger"
                size="sm"
                startContent={<TrashIcon className="h-4 w-4" />}
                onPress={handleDelete}
              >
                Delete ({selectedIds.length})
              </Button>
            )}
            <Button
              color="primary"
              size="sm"
              startContent={<PlusIcon className="h-4 w-4" />}
              onPress={openCreateModal}
            >
              Add Regular Pickup
            </Button>
          </div>
        </div>

        {/* Schedule Summary */}
        {showSummary && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              Today's Schedule Summary
            </h3>
            {scheduleSummary.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {scheduleSummary.map((item: any, idx: number) => (
                  <div key={idx} className="bg-white p-2 rounded border text-sm">
                    <span className="font-medium">{item.customer_code}</span> - {item.shipper_sh}
                    <br />
                    <span className="text-slate-500">Time: {item.preferredTime || "10:00"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No pickups scheduled for today</p>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === tableData.length && tableData.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Template</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Shipper</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Origin</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Destination</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Schedule</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Time</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {tableData.map((row: any) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="px-3 py-2 text-sm">{row.id}</td>
                  <td className="px-3 py-2 text-sm font-medium">{row.templateName}</td>
                  <td className="px-3 py-2 text-sm">{row.customer_code}</td>
                  <td className="px-3 py-2 text-sm">{row.shipper_sh}</td>
                  <td className="px-3 py-2 text-sm">{row.shipper_city}</td>
                  <td className="px-3 py-2 text-sm">{row.consignee_city}</td>
                  <td className="px-3 py-2 text-sm">
                    <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                      {getScheduleDisplay(row)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm">{row.preferredTime || "10:00"}</td>
                  <td className="px-3 py-2">
                    <Chip
                      size="sm"
                      color={row.isActive ? "success" : "default"}
                    >
                      {row.isActive ? "Active" : "Inactive"}
                    </Chip>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => handleGenerate(row.id)}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        title="Generate Pickup Now"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(row)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Delete this template?")) {
                            deleteMutate([row.id]);
                          }
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tableData.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-slate-500">
                    No regular pickups found. Click "Add Regular Pickup" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-slate-600">
              Showing {(currentPage - 1) * perPage + 1} to{" "}
              {Math.min(currentPage * perPage, itemCount)} of {itemCount} entries
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
                const page = Math.max(1, currentPage - 2) + i;
                if (page > pageCount) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 border rounded text-sm ${
                      currentPage === page ? "bg-primary text-white" : ""
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(Math.min(pageCount, currentPage + 1))}
                disabled={currentPage === pageCount}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto pt-16">
            <div className="flex min-h-full items-start justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-[90vw] transform overflow-visible rounded-2xl bg-gradient-to-br from-purple-400/5 via-blue-50 to-primary/10 p-4 shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-bold text-slate-800">
                        {isEditMode ? "Edit Regular Pickup Template" : "Create Regular Pickup Template"}
                      </span>
                    </div>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="p-1.5 hover:bg-slate-200/50 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>

                  {/* ═══════════ TOP ROW - Template & Customer ═══════════ */}
                  <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl mb-2">
                    <div className="px-3 py-2">
                      <div className="grid grid-cols-3 gap-x-3 gap-y-1">
                        <MInput label="Template" name="templateName" value={formData.templateName} onChange={handleInputChange} placeholder="e.g. ABC Corp Daily" />
                        <MSelect
                          label="Customer"
                          name="customer_code"
                          options={customerOptions}
                          value={customerOptions.find((c: any) => c.value === formData.customer_code)}
                          onChange={handleCustomerSelect}
                        />
                        <MSelect
                          label="Branch"
                          name="subbranch"
                          options={subbranchOptions}
                          value={formData.subbranch}
                          onChange={(opt: any) => setFormData((prev: any) => ({ ...prev, subbranch: opt }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* ═══════════ SHIPPER & CONSIGNEE - SIDE BY SIDE ═══════════ */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {/* Shipper Card */}
                    <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
                      <div className="p-0">
                        <div className="flex">
                          <div className="w-7 flex-shrink-0 bg-primary/50 rounded-l-xl flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SHIPPER</span>
                          </div>
                          <div className="flex-1 px-2.5 py-2 space-y-1">
                            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                              <MInput label="Mobile" name="shipper_mobile" value={formData.shipper_mobile} onChange={handleInputChange} />
                              <MInput
                                label="Pincode"
                                name="shipper_pincode"
                                value={formData.shipper_pincode}
                                onChange={handleInputChange}
                                onBlurExtra={(e: any) => {
                                  if (e.target.value?.length >= 6) lookupPincode({ pincode: e.target.value, field: "shipper" });
                                }}
                              />
                              <MInput label="S/H" name="shipper_sh" value={formData.shipper_sh} onChange={handleInputChange} />
                            </div>
                            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                              <MInput label="City" name="shipper_city" value={formData.shipper_city} onChange={handleInputChange} disabled />
                              <MInput label="State" name="shipper_state" value={formData.shipper_state} onChange={handleInputChange} disabled />
                              <MInput label="Country" name="shipper_country" value={formData.shipper_country} onChange={handleInputChange} disabled />
                            </div>
                            <MInput label="Address" name="shipper_address" value={formData.shipper_address} onChange={handleInputChange} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Consignee Card */}
                    <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
                      <div className="p-0">
                        <div className="flex">
                          <div className="w-7 flex-shrink-0 bg-amber-500/70 rounded-l-xl flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">CONSIGNEE</span>
                          </div>
                          <div className="flex-1 px-2.5 py-2 space-y-1">
                            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                              <MInput label="Mobile" name="consignee_mobile" value={formData.consignee_mobile} onChange={handleInputChange} />
                              <MInput
                                label="Pincode"
                                name="consignee_pincode"
                                value={formData.consignee_pincode}
                                onChange={handleInputChange}
                                onBlurExtra={(e: any) => {
                                  if (e.target.value?.length >= 6) lookupPincode({ pincode: e.target.value, field: "consignee" });
                                }}
                              />
                              <MInput label="Cgnee" name="consignee_cgnee" value={formData.consignee_cgnee} onChange={handleInputChange} />
                            </div>
                            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                              <MInput label="City" name="consignee_city" value={formData.consignee_city} onChange={handleInputChange} disabled />
                              <MInput label="State" name="consignee_state" value={formData.consignee_state} onChange={handleInputChange} disabled />
                              <MInput label="Country" name="consignee_country" value={formData.consignee_country} onChange={handleInputChange} disabled />
                            </div>
                            <MInput label="Company" name="consignee_company" value={formData.consignee_company} onChange={handleInputChange} />
                            <MInput label="Address" name="consignee_address" value={formData.consignee_address} onChange={handleInputChange} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ═══════════ PACKAGE & SCHEDULE - SIDE BY SIDE ═══════════ */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {/* Package Card */}
                    <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
                      <div className="p-0">
                        <div className="flex">
                          <div className="w-7 flex-shrink-0 bg-green-500/60 rounded-l-xl flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">PACKAGE</span>
                          </div>
                          <div className="flex-1 px-2.5 py-2 space-y-1">
                            <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                              <MInput label="Weight" name="weight" type="number" value={formData.weight} onChange={handleInputChange} />
                              <MInput label="Qty" name="quantity" type="number" value={formData.quantity} onChange={handleInputChange} />
                              <MSelect
                                label="Product"
                                name="product_name"
                                options={productOptions}
                                value={productOptions.find((p: any) => p.value === formData.product_name)}
                                onChange={(opt: any) => setFormData((prev: any) => ({ ...prev, product_name: opt?.value || "" }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Schedule Card */}
                    <div className="shadow-xl shadow-slate-200/50 bg-white/80 backdrop-blur-sm overflow-visible rounded-xl">
                      <div className="p-0">
                        <div className="flex">
                          <div className="w-7 flex-shrink-0 bg-blue-500/60 rounded-l-xl flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white tracking-wider [writing-mode:vertical-lr] rotate-180">SCHEDULE</span>
                          </div>
                          <div className="flex-1 px-2.5 py-2 space-y-1.5">
                            {/* Day Checkboxes */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <label className="text-[11px] font-medium text-slate-500 min-w-[52px] text-right">Days</label>
                              {[
                                { key: "sunday", label: "Su" },
                                { key: "monday", label: "Mo" },
                                { key: "tuesday", label: "Tu" },
                                { key: "wednesday", label: "We" },
                                { key: "thursday", label: "Th" },
                                { key: "friday", label: "Fr" },
                                { key: "saturday", label: "Sa" },
                              ].map((day) => (
                                <label
                                  key={day.key}
                                  className={`inline-flex items-center justify-center w-8 h-7 rounded-lg border text-[11px] font-semibold cursor-pointer transition-all ${
                                    formData[day.key]
                                      ? "bg-primary text-white border-primary shadow-sm"
                                      : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData[day.key]}
                                    onChange={(e) => setFormData((prev: any) => ({ ...prev, [day.key]: e.target.checked }))}
                                    className="sr-only"
                                  />
                                  {day.label}
                                </label>
                              ))}
                              <button type="button" onClick={toggleWeekdays} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200">M-S</button>
                              <button type="button" onClick={toggleAllDays} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200">All</button>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                              <MInput label="Time" name="preferredTime" type="time" value={formData.preferredTime} onChange={handleInputChange} />
                              <label className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-all cursor-pointer text-[11px] font-medium text-slate-600">
                                <input
                                  type="checkbox"
                                  name="isActive"
                                  checked={formData.isActive}
                                  onChange={handleInputChange}
                                  className="w-3.5 h-3.5 rounded border-slate-300 text-primary2 focus:ring-primary2/20"
                                />
                                Active
                              </label>
                            </div>
                            {/* WhatsApp Numbers */}
                            <div className="flex items-center gap-1.5">
                              <label className="text-[11px] font-medium text-slate-500 min-w-[52px] text-right">WhatsApp</label>
                              <input
                                type="text"
                                value={whatsappInput}
                                onChange={(e) => setWhatsappInput(e.target.value)}
                                placeholder="Phone number"
                                className="flex-1 min-w-0 px-2 py-[3px] rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-primary2 focus:ring-2 focus:ring-primary2/20"
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWhatsappNumber())}
                              />
                              <button type="button" onClick={addWhatsappNumber} className="text-[11px] px-2 py-[3px] bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">+Add</button>
                            </div>
                            {formData.whatsappNumbers.length > 0 && (
                              <div className="flex flex-wrap gap-1 ml-[58px]">
                                {formData.whatsappNumbers.map((num: string, idx: number) => (
                                  <span key={idx} className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                    {num}
                                    <button type="button" onClick={() => removeWhatsappNumber(num)} className="hover:text-red-600 font-bold">×</button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ═══════════ FOOTER - SAVE ═══════════ */}
                  <div className="shadow-xl shadow-slate-200/50 bg-slate-100/80 backdrop-blur-sm overflow-visible rounded-xl px-3 py-2">
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        size="sm"
                        color="default"
                        variant="flat"
                        className="h-8 text-xs font-semibold"
                        onPress={() => setIsModalOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#001f3f] to-[#003366] text-white font-bold rounded-lg shadow-lg shadow-[#001f3f]/30 h-8 text-xs tracking-wide hover:shadow-xl hover:shadow-[#001f3f]/40 transition-all px-6"
                        onPress={handleSubmit}
                        isLoading={isCreating || isUpdating}
                      >
                        {isEditMode ? "✏️ Update Template" : "🚀 Save Template"}
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default RegularPickupList;
